'use client';

import clsx from 'clsx';
import type { ReactNode } from 'react';

import { SDSLink } from '@repo/sds/components/SDSLink';
import { SDSPrimitiveButton } from '@repo/sds/components/SDSPrimitiveButton';

import '@repo/sds/components/SDSCard/SDSCard.scss';

export interface SDSCardProps {
  children: ReactNode;
  className?: string;
  dataTestId?: string;
  /** Navigation URL - renders card as a link (preferred for navigation). */
  href?: string;
  /** Click handler - renders card as a button (use for non-navigation actions). */
  onClick?: (e: React.SyntheticEvent) => void;
}

/**
 * SDSCard - A card container component.
 *
 * Renders as:
 * - `<a>` when `href` is provided (for navigation)
 * - `<button>` when `onClick` is provided (for actions)
 * - `<div>` when neither is provided (static card)
 */
export function SDSCard({ children, className, dataTestId, href, onClick }: SDSCardProps) {
  const cardClassName = clsx('SDSCard', (href || onClick) && 'SDSCard--clickable', className);

  // Link card (navigation) - preferred for routing
  if (href) {
    return (
      <SDSLink href={href} className={cardClassName} dataTestId={dataTestId}>
        {children}
      </SDSLink>
    );
  }

  // Button card (actions)
  if (onClick) {
    return (
      <SDSPrimitiveButton
        className={cardClassName}
        dataTestId={dataTestId}
        onClick={onClick}
        nativeButton={false}
        render={<div />}
      >
        {children}
      </SDSPrimitiveButton>
    );
  }

  // Static card
  return (
    <div className={cardClassName} data-testid={dataTestId}>
      {children}
    </div>
  );
}
